import React, { useMemo, useState } from "react"
import { useMutation, useQuery } from "urql"

import Breadcrumbs from "../../../components/shared/Breadcrumbs"
import { Button } from "../../../components/shared/Buttons"
import { ButtonLink } from "../../../components/shared/Links"
import LoadingSpinner from "../../../components/shared/LoadingSpinner"
import Typography from "../../../components/shared/Typography"
import GraphQLProvider from "../../../providers/GraphQLProvider"

const SUBSCRIBERS_QUERY = `
  query NewsletterSubscribers($status: String, $search: String) {
    newsletterSubscribers(status: $status, search: $search, limit: 100) {
      status
      source
      updatedAt
      preferencesUrl
      client {
        id
        name
        email
      }
    }
  }
`

const SET_SUBSCRIPTION_MUTATION = `
  mutation SetClientNewsletterSubscription($clientId: ID!, $status: String!) {
    setClientNewsletterSubscription(clientId: $clientId, status: $status) {
      result
      errors
      client {
        id
        newsletterSubscriptionStatus
      }
    }
  }
`

const formatUpdatedAt = (value) => {
  if (!value) return "—"
  return new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
}

const NewsletterSubscribers = () => {
  const [statusFilter, setStatusFilter] = useState("all")
  const [search, setSearch] = useState("")
  const [searchInput, setSearchInput] = useState("")
  const [{ data, fetching }, refetch] = useQuery({
    query: SUBSCRIBERS_QUERY,
    variables: { status: statusFilter === "all" ? null : statusFilter, search: search || null },
    requestPolicy: "cache-and-network"
  })
  const [, setSubscription] = useMutation(SET_SUBSCRIPTION_MUTATION)
  const [pendingClientId, setPendingClientId] = useState(null)

  const subscribers = useMemo(() => data?.newsletterSubscribers || [], [data])

  const handleSearch = (event) => {
    event.preventDefault()
    setSearch(searchInput.trim())
  }

  const toggleSubscription = async (clientId, currentStatus) => {
    const nextStatus = currentStatus === "subscribed" ? "unsubscribed" : "subscribed"
    setPendingClientId(clientId)
    await setSubscription({ clientId, status: nextStatus })
    refetch({ requestPolicy: "network-only" })
    setPendingClientId(null)
  }

  const copyPreferencesUrl = async (url) => {
    await navigator.clipboard.writeText(url)
  }

  return (
    <div>
      <Breadcrumbs links={[{ href: "/portal/settings", text: "Settings" }]} activePage="Newsletter subscribers" />
      <div className="mb-6 flex items-center justify-between gap-4 sm:flex-col sm:items-start">
        <div>
          <Typography as="h1" variant="h3">
            Newsletter subscribers
          </Typography>
          <Typography variant="body" className="text-gray-dark">
            See who can receive your practice newsletters and manage opt-in status.
          </Typography>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <ButtonLink href="/portal/settings/newsletter/campaigns" variant="secondary">
            Campaigns
          </ButtonLink>
          <ButtonLink href="/portal/settings/newsletter">Mailboxes</ButtonLink>
        </div>
      </div>

      <form onSubmit={handleSearch} className="mb-4 flex flex-wrap gap-2">
        <input
          type="search"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Search by name or email"
          className="min-w-[240px] flex-1 rounded-lg border border-gray-light px-3 py-2"
        />
        <Button type="primary">Search</Button>
      </form>

      <div className="mb-6 flex flex-wrap gap-2">
        {["all", "subscribed", "unsubscribed"].map((status) => (
          <Button
            key={status}
            type={statusFilter === status ? "primary" : "tertiary"}
            onClick={() => setStatusFilter(status)}>
            {status === "all" ? "All" : status.charAt(0).toUpperCase() + status.slice(1)}
          </Button>
        ))}
      </div>

      {fetching && !data ? (
        <LoadingSpinner />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-light">
          <table className="min-w-full divide-y divide-gray-light">
            <thead className="bg-gray-ultralight">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Client</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Email</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Source</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Last changed</th>
                <th className="px-4 py-3 text-left text-sm font-semibold" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-light bg-white">
              {subscribers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-dark">
                    No subscribers found.
                  </td>
                </tr>
              ) : (
                subscribers.map((row) => (
                  <tr key={row.client.id}>
                    <td className="px-4 py-3 text-sm">{row.client.name}</td>
                    <td className="px-4 py-3 text-sm">{row.client.email}</td>
                    <td className="px-4 py-3 text-sm capitalize">{row.status}</td>
                    <td className="px-4 py-3 text-sm">{row.source || "—"}</td>
                    <td className="px-4 py-3 text-sm text-gray-dark">{formatUpdatedAt(row.updatedAt)}</td>
                    <td className="px-4 py-3 text-right text-sm">
                      <div className="flex justify-end gap-2">
                        <Button
                          type="tertiary"
                          disabled={pendingClientId === row.client.id}
                          onClick={() => toggleSubscription(row.client.id, row.status)}>
                          {row.status === "subscribed" ? "Opt out" : "Opt in"}
                        </Button>
                        <Button type="tertiary" onClick={() => copyPreferencesUrl(row.preferencesUrl)}>
                          Copy manage link
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default function NewsletterSubscribersPage() {
  return (
    <GraphQLProvider>
      <NewsletterSubscribers />
    </GraphQLProvider>
  )
}
